// "Often added" suggestions: seeded items first, then items ranked by how often and
// how recently either of us adds them, with a boost for staples that look "due"
// (it has been at least as long as our usual gap between adds).

import type { AddEvent, ListItem, Staple } from './types.ts';
import { normalizeName } from './text.ts';

export interface Suggestion {
  name: string;
  stapleId?: string;
  qty: number;
  score: number;
  due: boolean;
}

const DAY = 86_400_000;
const HALF_LIFE_DAYS = 30;

export function suggest(opts: {
  staples: Staple[];
  events: AddEvent[];
  list: ListItem[];
  now?: Date;
  limit?: number;
}): Suggestion[] {
  const now = (opts.now ?? new Date()).getTime();
  const onList = new Set(opts.list.filter((i) => i.status === 'needed').map((i) => normalizeName(i.name)));

  // Group events by normalized name.
  const byName = new Map<string, AddEvent[]>();
  for (const e of opts.events) {
    const key = normalizeName(e.name);
    const arr = byName.get(key) ?? [];
    arr.push(e);
    byName.set(key, arr);
  }

  const candidates = new Map<string, Suggestion>();

  for (const s of opts.staples) {
    const key = normalizeName(s.name);
    const seedScore = s.seedRank != null ? 10 - Math.min(s.seedRank, 9) : 0;
    candidates.set(key, { name: s.name, stapleId: s.id, qty: s.usualQty, score: seedScore, due: false });
  }

  for (const [key, events] of byName) {
    const times = events.map((e) => new Date(e.at).getTime()).filter((t) => !Number.isNaN(t)).sort((a, b) => a - b);
    if (times.length === 0) continue;
    const recencyWeighted = times.reduce((sum, t) => sum + Math.pow(0.5, (now - t) / DAY / HALF_LIFE_DAYS), 0);
    let due = false;
    if (times.length >= 3) {
      const gaps = times.slice(1).map((t, i) => t - times[i]).sort((a, b) => a - b);
      const median = gaps[Math.floor(gaps.length / 2)];
      due = now - times[times.length - 1] >= median;
    }
    const existing = candidates.get(key);
    const last = events[events.length - 1];
    const score = (existing?.score ?? 0) + recencyWeighted * 3 + (due ? 5 : 0);
    candidates.set(key, {
      name: existing?.name ?? last.name,
      stapleId: existing?.stapleId ?? last.stapleId,
      qty: existing?.qty ?? 1,
      score,
      due,
    });
  }

  return [...candidates.entries()]
    .filter(([key]) => !onList.has(key))
    .map(([, s]) => s)
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score || a.name.localeCompare(b.name))
    .slice(0, opts.limit ?? 8);
}
